/**
 * @deprecated  Will be deleted soon. Use v2 instead.
 */

import React, { useEffect, useMemo, createContext, useContext, useState, useCallback } from 'react';
import { getApolloContext, ApolloCache, FetchResult } from '@apollo/client';
import { message } from 'antd';
import get from 'lodash/get';

import {
  useGetOrderItemsWithQuantityLazyQuery,
  GetCartQuery,
  useUpdateCartMutation,
  OrderItem,
  UpdateCartMutation,
  GetCartDocument,
  useGetCartLazyQuery,
} from '@hooks/cart/cart.gql.generated';
import { OrderItemConnectFragment } from '@hooks/cart';
import { useGetOrderItemLazyQuery } from '@hooks/orderItem';
import { PERFORMANCE_ITEM_PER_PAGE } from '@utils/constants';
import { UpdateCartOptions, CartContextType, CartControllerProps, CartStatus } from './types';
import { checkMustBuyWith, transformVariantByOrder } from './helper';
import { useAuth } from '@context/AuthContext';

const CartContext = createContext<CartContextType>({
  updateCount: 0,
  loading: false,
  initialLoading: false,
  isMiniCartItemDetailVisible: false,
  selectedCartItemId: '',
  selectedVariantId: undefined,
  variantIdWithCartData: {},
  handleUpdateCart: () => null,
  refetchAfterUpdatingCart: false,
  doneUpdatingCart: true,
  setDoneUpdatingCart: () => null,
  setSelectedCartItemId: () => null,
  refetchCart: () => null,
  fetchAllOrderItems: () => null,
  setImportData: () => [],
  cartStatus: '',
  setCartStatus: () => null,
  setVariantIdWithCartData: () => null,
  importData: [],
});

// store order in variantIdWithCartData as key value pair for easy access

export function CartController({ children }: CartControllerProps) {
  const [cartStatus, setCartStatus] = useState<CartStatus>('');
  const [updateCount, setUpdateCount] = useState(0);
  const [variantIdWithCartData, setVariantIdWithCartData] = useState({});
  const [importData, setImportData] = useState<[]>([]);
  const [isMiniCartItemDetailVisible, setMiniCartItemDetailVisibility] = useState(false);
  const [selectedCartItemId, setSelectedCartItemId] = useState<string | undefined>(undefined);
  const [selectedVariantId, setSelectedVariantId] = useState<string | undefined>(undefined);
  const [removingVariantId, setRemovingVariantId] = useState<string>('');
  const [isDeletion, setIsDeletion] = useState<boolean>(false);
  const [doneUpdatingCart, setDoneUpdatingCart] = useState(true);
  const { isLoggedIn } = useAuth();

  // get every order item because cart is paginated and all items's quantity are needed for instantdeal page
  const [fetchAllOrderItems] = useGetOrderItemsWithQuantityLazyQuery({
    fetchPolicy: 'no-cache',
    onCompleted: response => {
      const transformedData = transformVariantByOrder(response);
      setVariantIdWithCartData(transformedData);
    },
  });
  const { client } = useContext(getApolloContext());
  const [refetchAfterUpdatingCart, setRefetchAfterUpdatingCart] = useState(false);

  const [fetchCart, { data, loading, error, fetchMore, refetch: refetchCart }] = useGetCartLazyQuery({
    variables: { first: PERFORMANCE_ITEM_PER_PAGE },
    notifyOnNetworkStatusChange: true,
    onCompleted: response => {
      const uniqueIds: string[] = [];
      const unique = get(response, 'cart.orderItems.nodes', []).filter((element: OrderItem) => {
        const isDuplicate = uniqueIds.includes(element.id);
        if (!isDuplicate) {
          uniqueIds.push(element.id);
          return true;
        }
        return false;
      });
      client?.cache.writeQuery({
        variables: { first: PERFORMANCE_ITEM_PER_PAGE },
        query: GetCartDocument,
        data: {
          cart: {
            ...get(response, 'cart'),
            orderItems: {
              nodes: unique,
              pageInfo: get(response, 'cart.orderItems.pageInfo'),
              totalCount: get(response, 'cart.orderItems.totalCount'),
            },
          },
        },
      });
    },
  });

  useEffect(() => {
    if (isLoggedIn) {
      fetchCart();
    }
  }, [isLoggedIn]);

  const [fetchOrderItem, { data: orderItemData, loading: orderItemLoading }] = useGetOrderItemLazyQuery();
  const [updateCart, { loading: updateCartLoading, error: updateCartError }] = useUpdateCartMutation({
    onCompleted: () => setDoneUpdatingCart(true),
  });
  const initialLoading = loading && !data && !error;

  const handleUpdateCart = React.useCallback(
    async (variantId: string, quantity: number, options?: UpdateCartOptions) => {
      const currOrderItemId = quantity === 0 ? get(variantIdWithCartData, `${variantId}.orderItemId`, '') : '';
      setDoneUpdatingCart(false);
      try {
        await updateCart({
          variables: {
            input: { variantId, quantity },
            first: 1, // only get updated item
          },
          update: (
            cache: ApolloCache<UpdateCartMutation>,
            mutationResult: FetchResult<UpdateCartMutation, Record<string, any>, Record<string, any>>,
          ): void => {
            if (!mutationResult.data?.updateCart) return;

            fetchAllOrderItems();

            /*
              If currOrderItemId && quantity === 0  => delete variant by set quantity = 0
              => does not exist in variantIdWithCartData 
              => must delete by variantId in cache
            */
            if (currOrderItemId || (!currOrderItemId && quantity === 0)) {
              currOrderItemId && cache.evict({ id: `OrderItem:${currOrderItemId}` });
              const currentCartInCache = client?.cache.readQuery({
                variables: { first: PERFORMANCE_ITEM_PER_PAGE },
                query: GetCartDocument,
              });
              const currentOrderItemData: OrderItem[] = get(currentCartInCache, 'cart.orderItems.nodes', []);
              const newOrderItemData = currentOrderItemData.filter(orderItem => orderItem.variant.id !== variantId);
              client?.cache.writeQuery({
                variables: { first: PERFORMANCE_ITEM_PER_PAGE },
                query: GetCartDocument,
                data: {
                  cart: {
                    ...get(currentCartInCache, 'cart'),
                    orderItems: {
                      ...get(currentCartInCache, 'cart.orderItems'),
                      nodes: newOrderItemData,
                      totalCount: get(mutationResult, 'data.updateCart.orderItems.totalCount', 0),
                    },
                  },
                },
              });
            } else {
              const currentCartInCache = client?.cache.readQuery({
                variables: { first: PERFORMANCE_ITEM_PER_PAGE },
                query: GetCartDocument,
              });
              const updatedOrderItemId = get(mutationResult, 'data.updateCart.orderItems.nodes[0].id', '');
              let newOrderItemData: OrderItem[] = Object.assign(
                [],
                get(currentCartInCache, 'cart.orderItems.nodes', []),
              );
              if (
                !get(currentCartInCache, 'cart.orderItems.nodes').some((ref: OrderItem) => ref.id == updatedOrderItemId)
              ) {
                newOrderItemData.unshift(get(mutationResult, 'data.updateCart.orderItems.nodes[0]'));
              } else {
                newOrderItemData = newOrderItemData.filter(orderItem => orderItem.id !== updatedOrderItemId);
                newOrderItemData.unshift(get(mutationResult, 'data.updateCart.orderItems.nodes[0]'));
              }
              client?.cache.writeQuery({
                variables: { first: PERFORMANCE_ITEM_PER_PAGE },
                query: GetCartDocument,
                data: {
                  cart: {
                    ...get(currentCartInCache, 'cart'),
                    orderItems: {
                      nodes: newOrderItemData,
                      pageInfo: get(currentCartInCache, 'cart.orderItems.pageInfo'),
                      totalCount: get(mutationResult, 'data.updateCart.orderItems.totalCount', 0),
                    },
                  },
                },
              });
            }

            cache.gc(); // remove stale order item from Apollo InMemoryCache
          },
          ...options,
        });

        setIsDeletion(quantity === 0);
        setRemovingVariantId(variantId);
        setUpdateCount(prev => prev + 1);
        setRefetchAfterUpdatingCart(true);
      } catch (error) {
        message.error({ content: <span data-test="message-error">{(error as Error).message}</span> });
      }
    },
    [variantIdWithCartData],
  );

  const handleOpenMiniCartDetail = React.useCallback((id: string, variantId?: string) => {
    setSelectedCartItemId(id);
    setSelectedVariantId(variantId);
    setMiniCartItemDetailVisibility(true);
  }, []);

  const handleCloseMiniCartDetail = React.useCallback(() => {
    setSelectedCartItemId(undefined);
    setSelectedVariantId(undefined);
    setMiniCartItemDetailVisibility(false);
  }, []);

  const fetchMoreCartItem = React.useCallback(
    endCursor => {
      fetchMore?.({
        variables: {
          first: PERFORMANCE_ITEM_PER_PAGE,
          after: endCursor,
        },
        updateQuery: (
          prev: GetCartQuery,
          {
            fetchMoreResult,
          }: {
            fetchMoreResult?: GetCartQuery;
          },
        ): GetCartQuery => {
          const orderItems = get(fetchMoreResult, 'cart.orderItems.nodes', []);
          if (!orderItems.length) return prev;

          const previousOrderItems = get(prev, 'cart.orderItems.nodes', []);
          console.log('previousOrderItems:', previousOrderItems);
          const orderItemFragment = get(fetchMoreResult, 'cart.orderItems', {}) as OrderItemConnectFragment;
          console.log('orderItemFragment:', orderItemFragment);

          return {
            ...prev,
            cart: {
              ...prev.cart,
              orderItems: {
                ...orderItemFragment,
                nodes: [...previousOrderItems, ...orderItems],
              },
            },
          };
        },
      });
    },
    [fetchMore],
  );

  useEffect(() => {
    if (isLoggedIn) {
      refetchCart({
        first: PERFORMANCE_ITEM_PER_PAGE,
      });
    }

    if (isLoggedIn) {
      fetchAllOrderItems();
    }
  }, [isLoggedIn]);

  useEffect(() => {
    (async () => {
      const orderItemId = get(variantIdWithCartData, `${Number(selectedVariantId || 0)}.orderItemId`, null);
      if (orderItemId && isLoggedIn) {
        setSelectedCartItemId(orderItemId);
        fetchOrderItem({
          variables: {
            id: orderItemId,
          },
        });
      }
      const res = await client?.query({
        fetchPolicy: 'cache-only',
        query: GetCartDocument,
        variables: { first: PERFORMANCE_ITEM_PER_PAGE },
      });

      const firstItem = get(res, 'data.cart.orderItems.nodes[0]') as OrderItem;
      const quantity = get(res, 'data.cart.orderItems.nodes[0].quantity', 0);
      const variantId = get(firstItem, 'variant.id', '');
      if (!get(variantIdWithCartData, `${removingVariantId}`)) {
        // adding new cart item would cause variantIdWithCartData outdated
        if (!get(variantIdWithCartData, `${firstItem?.variant?.id}`, '')) {
          variantId &&
            setVariantIdWithCartData(prev => ({
              ...prev,
              [variantId]: {
                orderId: firstItem.id,
                quantity: firstItem.quantity,
                vatWholesalePrice: Number(firstItem.vatWholesalePrice),
              },
            }));
        }
      }

      firstItem &&
        updateCount > 0 &&
        checkMustBuyWith({
          variantIdWithCartData,
          currVariant: firstItem.variant,
          quantity: isDeletion ? 0 : quantity,
          callback: handleUpdateCart,
        });
      setRemovingVariantId('');
    })();
  }, [updateCount, isLoggedIn]);

  const context = useMemo(
    () => ({
      data,
      updateCount,
      setUpdateCount,
      orderItemData,
      initialLoading,
      loading,
      error,
      variantIdWithCartData,
      fetchMoreCartItem,
      handleUpdateCart,
      handleOpenMiniCartDetail,
      handleCloseMiniCartDetail,
      isMiniCartItemDetailVisible,
      selectedCartItemId,
      selectedVariantId,
      orderItemLoading,
      updateCartLoading,
      updateCartError,
      refetchAfterUpdatingCart,
      setRefetchAfterUpdatingCart,
      doneUpdatingCart,
      setVariantIdWithCartData,
      cartStatus,
      setCartStatus,
      setDoneUpdatingCart,
      setSelectedCartItemId,
      refetchCart,
      fetchAllOrderItems,
      setImportData,
      importData,
    }),
    [
      loading,
      initialLoading,
      data,
      updateCount,
      orderItemData,
      setVariantIdWithCartData,
      variantIdWithCartData,
      handleUpdateCart,
      setSelectedCartItemId,
      isMiniCartItemDetailVisible,
      selectedCartItemId,
      selectedVariantId,
      orderItemLoading,
      updateCartLoading,
      updateCartError,
      refetchAfterUpdatingCart,
      cartStatus,
      setCartStatus,
      doneUpdatingCart,
      setDoneUpdatingCart,
      refetchCart,
      fetchAllOrderItems,
      setImportData,
      importData,
    ],
  );

  return <CartContext.Provider value={context}>{children}</CartContext.Provider>;
}

export function useCartData() {
  return useContext(CartContext);
}

export function useFetchMoreCartItems() {
  const { data, loading, fetchMoreCartItem } = useCartData();
  const totalCount = get(data, 'cart.orderItems.totalCount');
  const products = get(data, 'cart.orderItems.nodes', []);
  const { hasNextPage, endCursor } = get(data, 'cart.orderItems.pageInfo', { hasNextPage: false, endCursor: '' });

  const fetchMoreProducts = useCallback(() => {
    fetchMoreCartItem?.(endCursor);
  }, [endCursor]);

  useEffect(() => {
    if (hasNextPage && products.length < totalCount && products.length % 10 !== 0) {
      fetchMoreProducts();
    }
  }, [products.length]);

  return { loading, hasNextPage, endCursor, fetchMoreProducts };
}

export const CartConsumer = CartContext.Consumer;
